import {getTealiumContext} from './tealiumContext';
import type {TealiumData} from './types';

const MAX_PAGE_NAME_LENGTH = 100;

const sanitizeSegment = (segment: string): string =>
  segment
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Construit page_name au format `<parentDomain>:<locale>:<...slugs>`.
 * - lowercase, espaces -> '-', join par ':' (cf. Page Name Strategy du doc)
 * - tronqué à 100 caractères
 *
 * Exemples :
 *  "/"             -> "trace+:en:home"
 *  "/fr/login"     -> "trace+:fr:login"
 *  "/watch/foo-bar"-> "trace+:en:watch:foo-bar"
 */
export const buildPageName = (path: string): string => {
  const {parentDomain, locale} = getTealiumContext();
  const cleanPath = (path || '/').split('?')[0].split('#')[0];
  const segments = cleanPath.split('/').map(sanitizeSegment).filter(Boolean);

  // Si la première section correspond déjà à la locale, on évite le doublon.
  if (segments[0] && segments[0] === locale) segments.shift();

  const parts = [
    parentDomain,
    locale,
    ...(segments.length ? segments : ['home']),
  ];
  const pageName = parts.join(':');
  return pageName.length > MAX_PAGE_NAME_LENGTH
    ? pageName.slice(0, MAX_PAGE_NAME_LENGTH)
    : pageName;
};

/** Renvoie le dernier slug significatif du path (ex. "login", "home", "watch"). */
export const buildPageSection = (path: string): string => {
  const cleanPath = (path || '/').split('?')[0].split('#')[0];
  const segments = cleanPath.split('/').map(sanitizeSegment).filter(Boolean);
  const last = segments[segments.length - 1];
  return last || 'home';
};

export interface BaseDataLayerInput {
  path: string;
  title?: string;
  eventName?: string;
  pageSection?: string;
  pageType?: string;
}

/**
 * Construit le data layer de base attendu par Tealium pour TRACE+.
 * Schéma issu de "Utag View All Pages" du doc Vodafone (Traceplus solution.xlsx).
 *
 * Les helpers du tracker peuvent ensuite étendre / surcharger ce payload.
 */
export const buildBaseDataLayer = (
  input: BaseDataLayerInput,
): TealiumData => {
  const ctx = getTealiumContext();
  const {path, title, eventName, pageType} = input;
  const url =
    typeof window !== 'undefined' && window.location ? window.location.href : '';
  const docTitle =
    title ||
    (typeof document !== 'undefined' && document.title ? document.title : '');
  const pageSection = input.pageSection || buildPageSection(path);

  const payload: TealiumData = {
    page_name: buildPageName(path),
    page_section: pageSection,
    page_parent_domain: ctx.parentDomain,
    page_country: ctx.country,
    page_url: url || path,
    page_locale: ctx.locale,
    page_title: docTitle,
    page_platform: ctx.platform,
    page_load: 1,
    site_version: ctx.siteVersion,
    site_type: ctx.siteType,
    visitor_id_asset_active: ctx.visitorId || '',
    visitor_login_status: ctx.loginStatus,
  };

  if (pageType) payload.page_type = pageType;
  if (eventName) payload.event_name = eventName;
  if (ctx.loyaltyPoints !== undefined) {
    payload.visitor_login_loyalty_points = ctx.loyaltyPoints;
  }
  if (ctx.loyaltyRedeemed !== undefined) {
    payload.visitor_login_loyalty_redeemed = ctx.loyaltyRedeemed;
  }

  return payload;
};
